import React, { useState } from 'react';
import type { CriticalResultsTimeSeriesBucket } from '../../types/criticalResultsDashboard.types';

interface CriticalResultsTrendChartProps {
  buckets: CriticalResultsTimeSeriesBucket[];
}

export default function CriticalResultsTrendChart({
  buckets,
}: Readonly<CriticalResultsTrendChartProps>) {
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);

  const chartMax = Math.max(...buckets.flatMap(bucket => [bucket.breaches, bucket.escalations]), 1);

  const yTicks = (() => {
    const step = Math.max(1, Math.ceil(chartMax / 4));
    const ticks: number[] = [];
    for (let value = 0; value <= chartMax; value += step) {
      ticks.unshift(value);
    }
    if (ticks[0] !== chartMax) {
      ticks.unshift(chartMax);
    }
    return [...new Set(ticks)].sort((a, b) => b - a);
  })();

  return (
    <section className="crd-panel crd-trend-chart">
      <header className="crd-panel-header">
        <h2 className="crd-panel-title">Breaches &amp; escalations over time</h2>
      </header>
      {buckets.length === 0 ? (
        <p className="crd-empty">No trend data for the selected filters.</p>
      ) : (
        <div className="crd-trend-body">
          <div
            className="crd-trend-y-axis"
            aria-hidden
          >
            {yTicks.map(tick => (
              <span
                key={`tick-${tick}`}
                className="crd-trend-y-tick"
              >
                {tick}
              </span>
            ))}
          </div>
          <div className="crd-trend-plot">
            <div
              className="crd-trend-grid"
              aria-hidden
            >
              {yTicks.map(tick => (
                <div
                  key={`grid-${tick}`}
                  className="crd-trend-grid-line"
                />
              ))}
            </div>
            <div className="crd-trend-bars">
              {buckets.map(bucket => {
                const breachPct = Math.max(
                  bucket.breaches > 0 ? 8 : 0,
                  (bucket.breaches / chartMax) * 100
                );
                const escalationPct = Math.max(
                  bucket.escalations > 0 ? 8 : 0,
                  (bucket.escalations / chartMax) * 100
                );
                const isHovered = hoveredLabel === bucket.periodLabel;
                return (
                  <button
                    type="button"
                    key={bucket.periodLabel}
                    className={[
                      'crd-trend-bar-group',
                      isHovered ? 'crd-trend-bar-group--hovered' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onMouseEnter={() => setHoveredLabel(bucket.periodLabel)}
                    onMouseLeave={() => setHoveredLabel(null)}
                    onFocus={() => setHoveredLabel(bucket.periodLabel)}
                    onBlur={() => setHoveredLabel(null)}
                    aria-label={`${bucket.periodLabel}: ${bucket.breaches} SLA breaches, ${bucket.escalations} escalations`}
                  >
                    {isHovered ? (
                      <div
                        className="crd-chart-tooltip crd-chart-tooltip--bar"
                        role="tooltip"
                      >
                        <p className="crd-chart-tooltip-title">{bucket.periodLabel}</p>
                        <p className="crd-chart-tooltip-row">
                          <span>SLA breaches</span>
                          <strong>{bucket.breaches}</strong>
                        </p>
                        <p className="crd-chart-tooltip-row">
                          <span>Escalations</span>
                          <strong>{bucket.escalations}</strong>
                        </p>
                      </div>
                    ) : null}
                    <div className="crd-trend-bar-pair">
                      <div className="crd-trend-bar-item">
                        <div className="crd-trend-bar-track">
                          <div
                            className="crd-trend-bar-fill crd-trend-bar-fill--breach"
                            style={{ height: `${breachPct}%` }}
                          />
                        </div>
                      </div>
                      <div className="crd-trend-bar-item">
                        <div className="crd-trend-bar-track">
                          <div
                            className="crd-trend-bar-fill crd-trend-bar-fill--escalation"
                            style={{ height: `${escalationPct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    <span className="crd-trend-bar-label">{bucket.periodLabel}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
